import { cx } from 'emotion';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { close } from '@ezugi/components/src/assets/icons';
import List from '@ezugi/primitives/src/List/List';
import Scrollable, { ScrollablePositionValues } from '@ezugi/primitives/src/Scrollable/Scrollable';
import { DeviceInfo } from '@ezugi/utils';

import { useOrientationChange, useFullScreenStatus } from '../../../hooks/src';
import Drawer from '../Drawer/Drawer';
import { IconType } from '../Drawer/types';
import {
  desktopHeader,
  greetingStyle,
  header,
  headingStyle,
  highlight,
  listContainer,
  logoStyle,
  menuContainer,
  menuDesktopDrawer,
  mobileHeader,
  mobileListContainer,
  noShadow,
  panelHeader,
  iOSClassName,
} from './Menu.css';

const LIST_ITEM_HEIGHT = 40;

type ScrollableInput = {
  clientHeight: number;
  scrollHeight: number;
  top: number;
}

const isScrollable = ({ clientHeight, scrollHeight, top }: ScrollableInput) => top < 0.92
&& clientHeight < scrollHeight;

const getLogoStyle = (logo: string) => ({ backgroundImage: `url(${logo})` });

type MenuLink = {
  icon: IconType;
  label: string;
  onClick: (e: React.MouseEvent) => void;
};

type MenuProps = {
  links: MenuLink[];
  align?: 'left' | 'right';
  logo: string;
  showHeader?: boolean;
  closeIcon?: IconType;
  onClose?: (e: React.MouseEvent<HTMLDivElement>) => void;
  nickname: string;
  greeting: string;
  nameBeforeGreeting: boolean;
  open: boolean;
  drawerClassName: string;
  onBackdropClick: (e: React.MouseEvent<HTMLDivElement>) => void;
};

const Menu = ({
  align = 'right',
  links,
  logo,
  nickname,
  greeting,
  nameBeforeGreeting,
  closeIcon = close,
  onClose = () => {},
  open,
  showHeader,
  drawerClassName,
  onBackdropClick,
}: MenuProps) => {
  const scrollRef = useRef<any>();
  const orientation = useOrientationChange();
  const isFullscreenEnabled = useFullScreenStatus();

  const [ canScroll, setCanScroll ] = useState(true);

  const onScrollUpdate = useCallback(
    (positionValues: ScrollablePositionValues) => {
      const prevState = canScroll;
      const nextState = isScrollable(positionValues);

      if (nextState !== prevState) {
        setCanScroll(nextState);
      }
    },
    [ canScroll ],
  );

  useEffect(() => {
    if (open && scrollRef.current) {
      setCanScroll(isScrollable(scrollRef.current.getValues()));
    }
  }, [ open, orientation ]);

  useEffect(() => {
    const { top } = scrollRef.current.getValues();
    scrollRef.current.scrollTop(top);
  }, [ isFullscreenEnabled ]);

  return (
    <Drawer
      onBackdropClick={onBackdropClick}
      open={open}
      align={align}
      {...(DeviceInfo.isHandheld
        ? {
          rightIcon: closeIcon,
          onRightIconClick: onClose,
          headerClassName: panelHeader,
          panelClassName: drawerClassName,
        }
        : {
          showBackdrop: false,
          showHeader,
          rightIcon: closeIcon,
          onRightIconClick: onClose,
          className: cx(menuDesktopDrawer, drawerClassName),
          panelStyle: { borderRadius: '10px' },
          headerClassName: panelHeader,
        })}
    >
      <div className={menuContainer}>
        <header
          className={cx(header, {
            [mobileHeader]: DeviceInfo.isHandheld,
            [desktopHeader]: !DeviceInfo.isHandheld,
          })}
        >
          {DeviceInfo.isHandheld && logo && (
            <div className={logoStyle} style={getLogoStyle(logo)} />
          )}
          {DeviceInfo.isHandheld ? (
            <h4 className={headingStyle}>{nickname}</h4>
          ) : (
            <span className={greetingStyle}>
              {!nameBeforeGreeting && [ greeting, ', ' ]}
              <span className={highlight}>{nickname}</span>
              {nameBeforeGreeting && [ ', ', greeting.toLowerCase() ]}
            </span>
          )}
        </header>
        <nav
          style={{
            ...(!DeviceInfo.isHandheld && {
              height: links.length * LIST_ITEM_HEIGHT,
            }),
          }}
          className={cx(
            listContainer,
            {
              [mobileListContainer]: DeviceInfo.isHandheld,
              [noShadow]: !canScroll,
            }
          )}
        >
          <Scrollable ref={scrollRef} onUpdate={onScrollUpdate} className={cx({ [iOSClassName]: DeviceInfo.isIOS })}>
            <List
              itemStyle={{ padding: '0 10px 0 20px', height: LIST_ITEM_HEIGHT }}
              items={links.map((l, i) => ({ ...l, id: i }))}
            />
          </Scrollable>
        </nav>
      </div>
    </Drawer>
  );
};

export default Menu;
